import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams?.get('email') || '';

  const [email, setEmail] = useState(emailParam);
  const [status, setStatus] = useState('idle'); // idle | loading | success | not_found | error
  const [error, setError] = useState('');
  const [optedOutOf, setOptedOutOf] = useState([]); // track what was actually opted out

  // Auto-submit if email is pre-filled from the link
  useEffect(() => {
    if (emailParam) {
      handleUnsubscribe(emailParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnsubscribe = async (targetEmail) => {
    const trimmed = (targetEmail || email)?.trim()?.toLowerCase();
    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setStatus('loading');

    const results = [];
    let foundAnything = false;

    try {
      // 1. Opt out of all email digest preferences for registered users
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, email_marketing, email_product_updates, email_quote_alerts, email_weekly_digest')
        ?.eq('email', trimmed)
        ?.maybeSingle();

      if (!profileError && profile) {
        foundAnything = true;
        const { error: updateError } = await supabase
          ?.from('user_profiles')
          ?.update({
            email_marketing: false,
            email_product_updates: false,
            email_quote_alerts: false,
            email_weekly_digest: false,
          })
          ?.eq('email', trimmed);

        if (!updateError) {
          results?.push('Marketing & Promotions');
          results?.push('Product Updates');
          results?.push('Quote Alerts');
          results?.push('Weekly Digest');
        }
      }

      // 2. Also remove from waitlist if present
      const { data: waitlistEntry, error: waitlistSelectError } = await supabase
        ?.from('waitlist')
        ?.select('id')
        ?.eq('email', trimmed)
        ?.maybeSingle();

      if (!waitlistSelectError && waitlistEntry) {
        foundAnything = true;
        const { error: deleteError } = await supabase
          ?.from('waitlist')
          ?.delete()
          ?.eq('email', trimmed);

        if (!deleteError) {
          results?.push('Waitlist communications');
        }
      }

      if (!foundAnything) {
        setStatus('not_found');
        return;
      }

      setOptedOutOf(results);
      setStatus('success');
    } catch (err) {
      console.error('[Unsubscribe] Unexpected error:', err);
      setStatus('error');
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    handleUnsubscribe(email);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #fff0f5 100%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/coming-soon-landing">
            <img
              src="/assets/images/Stackd_logo_design_no_background_colored_text-1772552765143.png"
              alt="Stackd logo"
              className="h-10 mx-auto"
            />
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-sm p-8">
          {status === 'success' ? (
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon name="CheckCircle" size={32} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-3">You've been unsubscribed</h1>
              <p className="text-text-secondary text-base leading-relaxed mb-4">
                Your email preferences have been updated for <strong className="text-foreground">{(emailParam || email)?.trim()?.toLowerCase()}</strong>.
              </p>
              {optedOutOf?.length > 0 && (
                <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-5 text-left">
                  <p className="text-sm font-semibold text-foreground mb-2">Opted out of:</p>
                  <ul className="space-y-1">
                    {optedOutOf?.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-text-secondary">
                        <Icon name="X" size={12} color="#F06F6B" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-text-secondary text-sm mb-6">
                Changed your mind?{' '}
                <Link to="/user-settings" className="font-semibold" style={{ color: '#F06F6B' }}>
                  Manage email preferences
                </Link>
              </p>
              <Link
                to="/coming-soon-landing"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon name="ArrowLeft" size={14} />
                Back to Stackd
              </Link>
            </div>
          ) : status === 'not_found' ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 bg-amber-100">
                <Icon name="SearchX" size={32} color="#d97706" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-3">Email not found</h1>
              <p className="text-text-secondary text-base leading-relaxed mb-6">
                We couldn't find <strong className="text-foreground">{email?.trim()?.toLowerCase()}</strong> in our system. It may have already been removed.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="text-sm font-semibold"
                style={{ color: '#F06F6B' }}
              >
                Try a different email
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-50">
                  <Icon name="BellOff" size={28} color="#F06F6B" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Unsubscribe</h1>
                <p className="text-text-secondary text-sm leading-relaxed">
                  Enter your email address to opt out of all Stackd email communications.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                      <Icon name="Mail" size={16} color="#F06F6B" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e?.target?.value); setError(''); }}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-text-secondary focus:outline-none focus:ring-2 transition-all duration-200 text-sm"
                      style={{ '--tw-ring-color': '#F58A54' }}
                      required
                    />
                  </div>
                  {error && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      {error}
                    </p>
                  )}
                  {status === 'error' && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      Something went wrong. Please try again.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                >
                  {status === 'loading' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="BellOff" size={16} />
                      <span>Unsubscribe from All Emails</span>
                    </>
                  )}
                </button>
              </form>

              <p className="text-center text-xs text-text-secondary mt-5">
                <Link to="/coming-soon-landing" className="hover:underline" style={{ color: '#F06F6B' }}>
                  ← Back to Stackd
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
